import { Component } from 'react'
import styled from 'styled-components'
import { FiClock, FiEdit2, FiTrash2, FiUsers, FiLock, FiCheckSquare, FiSquare } from 'react-icons/fi'
import { Banknote, TrendingDown } from 'lucide-react'
import { LocalStorage } from '../../Services'
import CalendarEventDetail from './CalendarEventDetail'
import { formatCurrency } from '../Tools/CurrencyConverter'

export default class EventList extends Component {
  constructor (props) {
    super(props)
    this.state = {
      detailEvent: null
    }
  }

  handleShowDetail = (event) => {
    this.setState({ detailEvent: event })
  }

  handleCloseDetail = () => {
    this.setState({ detailEvent: null })
  }

  render () {
    const { events, onEditEvent, onDeleteEvent, onToggleComplete } = this.props
    const { detailEvent } = this.state
    const currentUser = LocalStorage.get('user')
    const currentUserId = currentUser?._id

    if (!events || events.length === 0) {
      return <NoEvents>Nessun evento in questa data</NoEvents>
    }

    // Ordina: non completati prima, completati in fondo
    const sortedEvents = [...events].sort((a, b) => {
      if (a.isCompleted && !b.isCompleted) return 1
      if (!a.isCompleted && b.isCompleted) return -1
      // Stesso stato: ordina per ora
      return new Date(a.startDate) - new Date(b.startDate)
    })

    return (
      <>
        <EventsListContainer>
          {sortedEvents.map(event => (
            <EventItem
              key={event._id}
              event={event}
              currentUserId={currentUserId}
              onEdit={onEditEvent}
              onDelete={onDeleteEvent}
              onShowDetail={this.handleShowDetail}
              onToggleComplete={onToggleComplete}
            />
          ))}
        </EventsListContainer>

        {detailEvent && (
          <CalendarEventDetail
            event={detailEvent}
            onClose={this.handleCloseDetail}
          />
        )}
      </>
    )
  }
}

class EventItem extends Component {
  formatTime = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  formatDuration = (start, end) => {
    if (!start || !end) return ''
    const startDate = new Date(start)
    const endDate = new Date(end)
    const durationMs = endDate - startDate
    const hours = Math.floor(durationMs / (1000 * 60 * 60))
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
    }
    return `${minutes}m`
  }

  handleEdit = () => {
    if (this.props.onEdit) {
      this.props.onEdit(this.props.event)
    }
  }

  handleDelete = () => {
    if (this.props.onDelete) {
      this.props.onDelete(this.props.event)
    }
  }

  handleClick = () => {
    const { event, onShowDetail } = this.props
    // Se è auto-generato, mostra il dettaglio
    if (event.isAutoGenerated && onShowDetail) {
      onShowDetail(event)
    }
  }

  handleToggleComplete = (e) => {
    e.stopPropagation()
    const { event, onToggleComplete } = this.props
    if (onToggleComplete) {
      onToggleComplete(event)
    }
  }

  render () {
    const { event, currentUserId } = this.props
    const startTime = this.formatTime(event.startDate)
    const endTime = this.formatTime(event.endDate)
    const duration = event.isAllDay ? null : this.formatDuration(event.startDate, event.endDate)

    const isShared = event._isShared
    const isAllDay = event.isAllDay
    const isAutoGenerated = event.isAutoGenerated
    const eventType = event.eventType || 'manual'
    const hasAmount = event.amount !== null && event.amount !== undefined
    const isCompleted = event.isCompleted

    // L'utente può modificare/eliminare solo se è il creatore e NON è auto-generato
    const isCreator = isShared
      ? event._createdBy?._id === currentUserId
      : true // Eventi privati (non condivisi) sono sempre dell'utente

    const canEdit = isCreator && !isAutoGenerated

    // Può de-spuntare solo chi ha spuntato
    const canUncheck = !isCompleted || event.completedBy?._id === currentUserId
    const completedByName = event.completedBy ? `${event.completedBy.nome} ${event.completedBy.cognome}` : null

    return (
      <EventItemContainer
        onClick={this.handleClick}
        $clickable={isAutoGenerated}
        $eventType={eventType}
        $color={event.displayColor || event.colorId || 'primary'}
        $isCompleted={isCompleted}
      >
        <CheckboxContainer
          onClick={this.handleToggleComplete}
          $isCompleted={isCompleted}
          $canUncheck={canUncheck}
          title={!canUncheck ? `Solo ${completedByName} può annullare` : (isCompleted ? 'Segna come da fare' : 'Segna come fatto')}
        >
          {isCompleted ? <FiCheckSquare /> : <FiSquare />}
        </CheckboxContainer>

        <EventContent>
          <EventHeader>
            <EventTime>
              <FiClock />
              {isAllDay
                ? (
                  <span>Tutto il giorno</span>
                  )
                : (
                  <>
                    <span>{startTime}</span>
                    {endTime && <span>- {endTime}</span>}
                    {duration && <Duration>({duration})</Duration>}
                  </>
                  )}
            </EventTime>

            {canEdit && (
              <EventActions>
                <ActionButton onClick={this.handleEdit} title='Modifica evento'>
                  <FiEdit2 />
                </ActionButton>
                <ActionButton onClick={this.handleDelete} title='Elimina evento' $danger>
                  <FiTrash2 />
                </ActionButton>
              </EventActions>
            )}
          </EventHeader>

          <EventTitle $isCompleted={isCompleted}>{event.title || 'Senza titolo'}</EventTitle>

          {isCompleted && completedByName && (
            <CompletedInfo>
              Completato da {completedByName} il {new Date(event.completedAt).toLocaleDateString('it-IT')}
            </CompletedInfo>
          )}

          {event.description && (
            <EventDescription>{event.description}</EventDescription>
          )}

          <EventFooter>
            {isCompleted && (
              <EventTypeBadge $type='completed'>
                <FiCheckSquare size={14} />
                <span>Fatto</span>
              </EventTypeBadge>
            )}

            {eventType === 'cedola' && (
              <EventTypeBadge $type='cedola'>
                <Banknote size={14} />
                <span>Cedola</span>
              </EventTypeBadge>
            )}

            {eventType === 'costo' && (
              <EventTypeBadge $type='costo'>
                <TrendingDown size={14} />
                <span>Costo</span>
              </EventTypeBadge>
            )}

            {isShared
              ? (
                <SharedBadge>
                  <FiUsers />
                  <span>Condiviso</span>
                  {event._createdBy && (
                    <CreatorName>da {event._createdBy.nome}</CreatorName>
                  )}
                </SharedBadge>
                )
              : eventType === 'manual' && (
                <PrivateBadge>
                  <FiLock />
                  <span>Privato</span>
                </PrivateBadge>
              )}

            {event.location && (
              <LocationBadge title={event.location}>
                📍 {event.location}
              </LocationBadge>
            )}

            {hasAmount && (
              <AmountBadge $type={eventType}>
                {formatCurrency(event.amount, event.currency)}
              </AmountBadge>
            )}
          </EventFooter>
        </EventContent>
      </EventItemContainer>
    )
  }
}

// ============ STYLED COMPONENTS ============

const EventsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const NoEvents = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.text.muted};
  font-size: 0.875rem;
  padding: 2rem;
  margin: 0;
`

const EventItemContainer = styled.div`
  display: flex;
  background: ${props => props.theme.colors.background.hover};
  border-radius: ${props => props.theme.radius.input};
  border-left: 4px solid ${props => {
    if (props.$isCompleted) return props.theme.colors.semantic.success
    if (props.$eventType === 'cedola') return '#10B981'
    if (props.$eventType === 'costo') return '#EF4444'

    // Gestione colori Google Calendar per eventi manuali
    if (props.$color && props.$color.startsWith('#')) {
      return props.$color
    }

    const colors = {
      1: '#7986CB',
      2: '#33B679',
      3: '#8E24AA',
      4: '#E67C73',
      5: '#F6BF26',
      6: '#F4511E',
      7: '#039BE5',
      8: '#616161',
      9: '#3F51B5',
      10: '#0B8043',
      11: '#D50000',
      primary: props.theme.colors.semantic.primary
    }
    return colors[props.$color] || colors.primary
  }};
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  min-height: 80px;
  flex-shrink: 0;
  opacity: ${props => props.$isCompleted ? 0.6 : 1};

  &:hover {
    background: ${props => props.theme.colors.background.secondary};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px ${props => props.theme.colors.shadow?.light || 'rgba(0, 0, 0, 0.08)'};
    opacity: ${props => props.$isCompleted ? 0.8 : 1};
  }
`

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.75rem;
  font-size: 1.25rem;
  color: ${props => props.$isCompleted
    ? props.theme.colors.semantic.success
    : props.theme.colors.text.tertiary};
  cursor: ${props => props.$canUncheck ? 'pointer' : 'not-allowed'};
  opacity: ${props => props.$canUncheck ? 1 : 0.5};
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    color: ${props => props.$canUncheck
      ? props.theme.colors.semantic.success
      : props.theme.colors.text.tertiary};
    transform: ${props => props.$canUncheck ? 'scale(1.1)' : 'none'};
  }
`

const EventContent = styled.div`
  flex: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const EventHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const EventTime = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.813rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.secondary};

  svg {
    font-size: 0.875rem;
  }

  span {
    white-space: nowrap;
  }
`

const Duration = styled.span`
  font-weight: 400;
  color: ${props => props.theme.colors.text.tertiary};
`

const EventActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${EventItemContainer}:hover & {
    opacity: 1;
  }
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: 1px solid ${props => props.$danger ? props.theme.colors.semantic.danger : props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.button};
  color: ${props => props.$danger ? props.theme.colors.semantic.danger : props.theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 0.75rem;
  }

  &:hover {
    background: ${props => props.$danger ? props.theme.colors.semantic.danger : props.theme.colors.background.secondary};
    color: ${props => props.$danger ? props.theme.colors.text.inverse : props.theme.colors.text.primary};
    border-color: ${props => props.$danger ? props.theme.colors.semantic.danger : props.theme.colors.border.strong};
  }

  &:active {
    transform: scale(0.95);
  }
`

const EventTitle = styled.h4`
  font-size: 0.938rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
  line-height: 1.4;
  text-decoration: ${props => props.$isCompleted ? 'line-through' : 'none'};
  opacity: ${props => props.$isCompleted ? 0.7 : 1};
`

const CompletedInfo = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.semantic.success};
  margin: 0;
  font-style: italic;
`

const EventDescription = styled.p`
  font-size: 0.813rem;
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const EventFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: auto;
`

const SharedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: ${props => props.theme.colors.semantic.info};
  background: ${props => props.theme.colors.semantic.info}15;
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.radius.button};
  font-weight: 500;

  svg {
    font-size: 0.75rem;
  }
`

const PrivateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: ${props => props.theme.colors.text.tertiary};
  background: ${props => props.theme.colors.background.secondary};
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.radius.button};
  font-weight: 500;

  svg {
    font-size: 0.75rem;
  }
`

const CreatorName = styled.span`
  opacity: 0.8;
`

const EventTypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: ${props => {
    if (props.$type === 'completed') return props.theme.colors.semantic.success
    if (props.$type === 'cedola') return '#10B981'
    if (props.$type === 'costo') return '#EF4444'
    return props.theme.colors.text.tertiary
  }};
  background: ${props => {
    if (props.$type === 'completed') return `${props.theme.colors.semantic.success}15`
    if (props.$type === 'cedola') return '#10B98115'
    if (props.$type === 'costo') return '#EF444415'
    return props.theme.colors.background.secondary
  }};
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.radius.button};
  font-weight: 600;

  svg {
    font-size: 0.75rem;
  }
`

const LocationBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: ${props => props.theme.colors.text.tertiary};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const AmountBadge = styled.div`
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: ${props => {
    if (props.$type === 'cedola') return '#10B981'
    if (props.$type === 'costo') return '#EF4444'
    return props.theme.colors.text.primary
  }};
  padding: 0.25rem 0.625rem;
  background: ${props => {
    if (props.$type === 'cedola') return '#10B98120'
    if (props.$type === 'costo') return '#EF444420'
    return props.theme.colors.background.secondary
  }};
  border-radius: ${props => props.theme.radius.button};
  margin-left: auto;
`
